import {IEditor, IMedia, IOwner} from "@webng-types/write-model";
import {MediaGw, type ImageTransform} from "@tickaroo/media-transform";

// Re-exported so consumers of @webng/liveblog (e.g. the embedjs-backend AMP
// templates) can size srcsets against the transformed dimensions without taking
// a direct dependency on @tickaroo/media-transform.
export {effectiveSourceDimensions} from "@tickaroo/media-transform";
export type {ImageTransform, Crop} from "@tickaroo/media-transform";

// This is hacky in here

interface IMediaWithSync extends IMedia {
  syncMedia: any
}

function isWithSyncMedia(media: IMedia|IMediaWithSync|undefined): media is IMediaWithSync {
  // noinspection RedundantIfStatementJS
  if(media && (media as IMediaWithSync).syncMedia !== undefined) {
    return true
  } else {
    return false;
  }
}


interface SourceImage {
  url: string
  width?: number
  height?: number
}

interface TargetImage {
  width: number
  height: number
  fit?: 'null'|'cover'|'contain'|'limit'
  format?: 'webp'|'jpeg'|'png'
}

export interface ReturnImage {
  url: string
  width: number
  height: number
}

function placeholderSource(subtype?: string): SourceImage {
  if(subtype === 'v') {
    return {
      url: "iconpack://pending_video.svg",
      width: 96,
      height: 96
    }
  } else {
    return {
      url: "iconpack://pending_image.svg",
      width: 96,
      height: 96
    }
  }
}

export function thumbnailSource(media?: IMedia): SourceImage {
  if(media) {
    if (media.subtype === 'v') {
      return videoThumbnailSource(media);
    } else {
      return mediaThumbnailSource(media);
    }
  } else {
    return placeholderSource()
  }
}

export function mediaThumbnailSource(media: IMedia): SourceImage {
  if(media.url) {
    return {
      url: media.url,
      width: media.width,
      height: media.height
    }
  } else if(isWithSyncMedia(media) && media.syncMedia?.previewStatus === "success") {
    return {
      url: media.syncMedia.thumbnailUrl!,
      width: media.syncMedia.width,
      height: media.syncMedia.height
    }
  } else {
    return placeholderSource("i")
  }
}


export function ownerThumbnailSource(owner: IOwner): SourceImage|undefined {
  if(owner.image) {
    return {
      url: owner.image
    }
  } else {
    return undefined
  }
}

export function editorThumbnail(editor: IEditor): ReturnImage|undefined {
  if(editor.image) {
    return exactSizeImageUrl(editor.image, {width: 100, height: 100})
  } else {
    return undefined
  }
}

// A raw video file (an RSS content-stream preview before the server has transcoded it) has no
// derivable poster; only the two transcoder URL shapes below do.
const DIRECT_VIDEO_FILE = /\.(mp4|m4v|mov|webm)([?#]|$)/i

export function isDirectVideoFileUrl(url: string | undefined): boolean {
  return url !== undefined && DIRECT_VIDEO_FILE.test(url)
}

// Preview set for a video that has no derivable poster at all (content-stream preview of a feed URL).
export function pendingVideoPreviews(): ReturnImage[] {
  const source = placeholderSource("v")
  return [160, 320, 640, 960].map(width => aspectWidthSizedImageUrl(source, width, {format: 'webp'}))
}

export function videoThumbnailSource(media: IMedia): SourceImage {
  if(!media.needs_upload_from_device && media.url) {
    if(media.url.startsWith("imageservice://media/")) { // aws mediaconvert style
      return {
        url: media.url + ".raw.0000000.jpg",
        width: media.width,
        height: media.height
      }
    } else if(!isDirectVideoFileUrl(media.url)) { // zencoder style
      return {
        url: media.url + "/frame_0002.jpg",
        width: media.width,
        height: media.height
      }
    } else {
      // No derivable poster, but the declared dimensions (if any) still drive the player's aspect ratio;
      // the placeholder's own 96×96 must not masquerade as the video's size.
      return {
        url: placeholderSource("v").url,
        width: media.width,
        height: media.height
      }
    }
  } else if(isWithSyncMedia(media) && media.syncMedia?.previewStatus === "success") {
    return {
      url: media.syncMedia.thumbnailUrl!,
      width: media.syncMedia.width,
      height: media.syncMedia.height
    }
  } else {
    return placeholderSource("v")
  }
}


export function aspectWidthSizedImageUrl(source: SourceImage, targetWidth: number, options: Omit<TargetImage, "width"|"height"> = {}, transform?: ImageTransform): ReturnImage {
  // `source` must already carry the effective (cropped-and-rotated) dimensions so
  // the derived target height reflects the transformed aspect; `transform` is
  // forwarded so exactSizeImageUrl emits the rotate/crop filters.
  if(source.width && source.height) {
    // Floor to >=1: a very thin crop can drive the derived height to 0, which the
    // proxy rejects (h_0) and which is a degenerate layout box. No-op for real
    // sizes, which are always >= 1.
    const targetHeight = Math.max(1, Math.floor((targetWidth/source.width) * source.height));
    return exactSizeImageUrl(source.url, {width: targetWidth, height: targetHeight, ...options}, transform);
  } else {
    // noinspection JSSuspiciousNameCombination
    return exactSizeImageUrl(source.url, {width: targetWidth, height: targetWidth, ...options}, transform);
  }
}

// export function aspectHeightSizedImageUrl(source: SourceImage, targetHeight: number): ReturnImage {
//   if(source.width && source.height) {
//     const targetWidth = Math.floor((targetHeight/source.height) * source.width);
//     return exactSizeImageUrl(source.url, {width: targetWidth, height: targetHeight});
//   } else {
//     // noinspection JSSuspiciousNameCombination
//     return exactSizeImageUrl(source.url, {width: targetHeight, height: targetHeight});
//   }
// }
//
export function aspectFitSizedImageUrl(source: SourceImage, target: TargetSize, options: Omit<TargetImage, "width"|"height"> = {}, transform?: ImageTransform): ReturnImage {
  // `source` must carry the effective (cropped-and-rotated) dimensions so the
  // fitted box matches the transformed aspect; `transform` is forwarded so the
  // proxy emits the rotate/crop filters. Passing no transform is byte-identical
  // to the legacy behaviour.
  const size = aspectFit(source, target)
  return exactSizeImageUrl(source.url, {...size, ...options}, transform)
}

export function exactSizeImageUrl(url: string, target: TargetImage, transform?: ImageTransform): ReturnImage {
  let parsedUrl: URL;

  try {
    parsedUrl = new URL(url);
  } catch(e) {
    parsedUrl = new URL("tik-iconpack://webembed_failed.svg")
  }

  if (parsedUrl.protocol === 'blob:' || parsedUrl.protocol === "data:") {
    return {
      ...target,
      url
    };
  } else if(parsedUrl.protocol === 'iconpack:' || parsedUrl.protocol === 'tik-iconpack:') {
    // there are some browser differences in parsing (pathname = chrome, hostname = safari)
    const icon = parsedUrl.pathname ? parsedUrl.pathname.substr(2) : parsedUrl.hostname
    return {
      ...target,
      url: "/iconpack/" + icon
    }
  } else {
    // URL construction delegates to the shared @tickaroo/media-transform builder.
    // Filter order: rotate -> user-crop (from the placement's transform) -> resize
    // -> format. When no transform is present, the output is byte-identical to the
    // legacy inline builder (resize + format only).
    const builder = MediaGw.image(url)
    if (transform) {
      builder.applyTransform(transform)
    }
    if (target.fit !== 'null') {
      builder.resize(target.width, target.height, {fit: target.fit})
    }
    if (target.format) {
      builder.format(target.format)
    }

    return {
      ...target,
      url: builder.build(),
    }
  }
}

export interface VideoFrameOptions {
  access_token?: string
  notrack?: "true"
  autoplay?: "true"
  client_id?: string
}

export function videoFrameUrl(gameId: string, eventId: string, mediaId: string, opts: VideoFrameOptions): string {
  const query = new URLSearchParams(opts as Record<string,string>).toString()
  return `/embed/v4/video-frame/${encodeURIComponent(gameId)}/${encodeURIComponent(eventId)}/${encodeURIComponent(mediaId)}.html?${query}`
}

export enum VideoVariant {
  SD,
  HD
}

interface SourceSize {width?: number, height?: number}
export interface TargetSize {width: number, height: number}

export function aspectFit(tSource: SourceSize, target: TargetSize): TargetSize {
  if(tSource.width && tSource.height) {
    if (tSource.width/tSource.height < target.width/target.height) {
      return {
        width: Math.floor((target.height/tSource.height) * tSource.width),
        height: target.height
      }
    } else {
      return {
        width: target.width,
        height: Math.floor((target.width/tSource.width) * tSource.height)
      }
    }
  } else {
    return target;
  }
}


// v2/mobile/advanced: {"size": "1280x720", "audio_bitrate": 56, "max_audio_sample_rate": 48000, "max_frame_rate": 30, "max_video_bitrate": 5000, "video_codec_level": 3.1, "videoe_codec_profile": "main"}
// v2/mobile/baseline: {"size": "480x320", "audio_bitrate": 96, "max_audio_sample_rate": 44100, "max_frame_rate": 30, "max_video_bitrate": 1500, "video_reference_frames": 1}
// v2/mobile/legacy: {"size": "320x240", "audio_bitrate": 96, "max_audio_sample_rate": 44100, "max_frame_rate": 30, "max_video_bitrate": 768, "video_codec_level": 1.3, "video_reference_frames": 1}

export function videoVariant(media: IMedia, tSource: SourceSize, variant: VideoVariant) {
  if(media.url?.startsWith("imageservice://media/")) { // new style aws target
    switch (variant) {
      case VideoVariant.SD:
        return {
          ...aspectFit(tSource, {width: 480, height: 320}),
          url: media.url?.replace('imageservice:/', '') + ".sd.mp4"
        }
      case VideoVariant.HD:
        return {
          ...aspectFit(tSource, {width: 1280, height: 720}),
          url: media.url?.replace('imageservice:/', '') + ".hd.mp4"
        }
    }
  } else if(media.url?.startsWith("imageservice")) { // old style zencoder target
    const encodedId = 'media-' + encodeURIComponent(media.local_id!);

    switch (variant) {
      case VideoVariant.SD:
        return {
          ...aspectFit(tSource, {width: 480, height: 320}),
          url: "/" + encodedId + "/mobile_baseline.mp4"
        }
      case VideoVariant.HD:
        return {
          ...aspectFit(tSource, {width: 1280, height: 720}),
          url: "/" + encodedId + "/mobile_advanced.mp4"
        }
    }
  } else if(media.url?.startsWith("http")) { // external hosted
    return {
      ...tSource,
      url: media.url
    }
  } else {
    return {
      ...tSource,
      url: undefined
    }
  }
}
